import React, { useState } from 'react';
import { CloseIcon, ChevronDownIcon, ChevronUpIcon } from '../icons/Icons';
import { getImageUrl } from '../../config/api';

/**
 * ============================================
 * Bélanger-Style Product Modal
 * ============================================
 * Clean, minimal product detail view with expandable variants showing rolls
 */

const ProductModal = ({ product, isOpen, onClose }) => {
    const [expandedVariants, setExpandedVariants] = useState(new Set());
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    if (!isOpen || !product) return null;

    // Build gallery array: main image + variant images
    const galleryImages = [
        {
            url: getImageUrl(product.mainImage) || getImageUrl(product.image) || product.images?.[0],
            type: 'main',
            label: null
        },
        ...(product.variants || [])
            .filter(variant => variant.image)
            .map(variant => ({
                url: getImageUrl(variant.image),
                type: 'variant',
                label: variant.colorName,
                color: variant.color
            }))
    ].filter(img => img.url);

    const currentImage = galleryImages[currentImageIndex] || galleryImages[0];

    const toggleVariantExpansion = (index) => {
        setExpandedVariants(prev => {
            const newSet = new Set(prev);
            if (newSet.has(index)) {
                newSet.delete(index);
            } else {
                newSet.add(index);
            }
            return newSet;
        });
    };

    const goToPrevImage = () => {
        setCurrentImageIndex(prev => prev === 0 ? galleryImages.length - 1 : prev - 1);
    };

    const goToNextImage = () => {
        setCurrentImageIndex(prev => prev === galleryImages.length - 1 ? 0 : prev + 1);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
            {/* Modal Container */}
            <div 
                className="relative w-full max-w-5xl max-h-[90vh] bg-white overflow-hidden shadow-2xl"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="absolute top-6 right-6 z-10 p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                    <CloseIcon className="w-6 h-6 text-gray-900" />
                </button>

                <div className="flex flex-col lg:flex-row max-h-[90vh]">
                    {/* Image Section */}
                    <div className="lg:w-1/2 bg-gray-50 relative">
                        <div className="h-full flex items-center justify-center p-8 md:p-12">
                            {currentImage?.url ? (
                                <img
                                    src={currentImage.url}
                                    alt={product.name}
                                    className="max-w-full max-h-[500px] object-contain"
                                />
                            ) : (
                                <div className="text-center text-gray-400">
                                    <svg className="w-32 h-32 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                    </svg>
                                    <p className="text-sm">Aucune image disponible</p>
                                </div>
                            )}
                        </div>

                        {/* Color Badge - Only show for variant images */}
                        {currentImage?.type === 'variant' && (
                            <div className="absolute top-4 left-4">
                                <div className="flex items-center gap-2 bg-white/95 backdrop-blur-sm px-3 py-2 rounded-full shadow-lg">
                                    <div 
                                        className="w-4 h-4 rounded-full border border-gray-300"
                                        style={{ backgroundColor: currentImage.color || '#888' }}
                                    />
                                    <span className="text-xs font-medium text-gray-900">
                                        {currentImage.label}
                                    </span>
                                </div>
                            </div>
                        )}

                        {/* Navigation Buttons - Only show if more than 1 image */}
                        {galleryImages.length > 1 && (
                            <>
                                {/* Previous Button */}
                                <button
                                    onClick={goToPrevImage}
                                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 md:p-3 rounded-full shadow-lg transition-all z-10"
                                >
                                    <svg className="w-5 h-5 md:w-6 md:h-6 text-gray-900" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                    </svg>
                                </button>

                                {/* Next Button */}
                                <button
                                    onClick={goToNextImage}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 md:p-3 rounded-full shadow-lg transition-all z-10"
                                >
                                    <svg className="w-5 h-5 md:w-6 md:h-6 text-gray-900" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                </button>

                                {/* Image Indicator Dots */}
                                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                                    {galleryImages.map((_, index) => (
                                        <button
                                            key={index}
                                            onClick={() => setCurrentImageIndex(index)}
                                            className={`transition-all ${
                                                index === currentImageIndex
                                                    ? 'w-8 h-2 bg-gray-900'
                                                    : 'w-2 h-2 bg-gray-400 hover:bg-gray-600'
                                            } rounded-full`}
                                        />
                                    ))}
                                </div>
                            </>
                        )}

                        {/* Image Counter */}
                        {galleryImages.length > 1 && (
                            <div className="absolute top-4 right-4 bg-black/70 text-white px-3 py-1 rounded-full text-xs font-medium">
                                {currentImageIndex + 1} / {galleryImages.length}
                            </div>
                        )}
                    </div>

                    {/* Details Section */}
                    <div className="lg:w-1/2 p-8 lg:p-12 overflow-y-auto">
                        {/* Header */}
                        <div className="mb-8">
                            <h2 className="text-2xl font-serif text-gray-900 mb-3">
                                {product.name}
                            </h2>
                        </div>

                        {/* Product Info */}
                        <div className="mb-8 pb-8 border-b border-gray-200">
                            <div>
                                <p className="text-xs text-gray-500 mb-1 uppercase">Couleurs</p>
                                <p className="text-lg font-semibold text-gray-900">{product.variants?.length || 0}</p>
                            </div>
                        </div>

                        {/* Variants Section with Expandable Rolls */}
                        {product.variants && product.variants.length > 0 && (
                            <div className="mb-8">
                                <h4 className="text-sm font-semibold text-gray-900 mb-4 uppercase tracking-wide">
                                    Couleurs disponibles
                                </h4>
                                <div className="space-y-2">
                                    {product.variants.map((variant, index) => {
                                        const isExpanded = expandedVariants.has(index);
                                        const totalStock = variant.totalStock || variant.rolls?.reduce((sum, r) => sum + (r.length || 0), 0) || 0;
                                        const rollCount = variant.rolls?.length || 0;
                                        
                                        return (
                                            <div key={index} className="border border-gray-200">
                                                {/* Variant Header - Clickable */}
                                                <button
                                                    onClick={() => toggleVariantExpansion(index)}
                                                    className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <div 
                                                            className="w-8 h-8 rounded-full border-2 border-gray-300"
                                                            style={{ backgroundColor: variant.color || '#888' }}
                                                        />
                                                        <div className="text-left">
                                                            <span className="text-sm font-medium text-gray-900 block">
                                                                {variant.colorName || 'Couleur'}
                                                            </span>
                                                            <span className="text-xs text-gray-500">
                                                                Réf: {variant.reference || 'N/A'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-4">
                                                        <div className="text-right">
                                                            <p className="text-sm font-semibold text-gray-900">
                                                                {totalStock}m
                                                            </p>
                                                            <p className="text-xs text-gray-500">
                                                                {rollCount} rouleau{rollCount > 1 ? 'x' : ''}
                                                            </p>
                                                        </div>
                                                        {isExpanded ? (
                                                            <ChevronUpIcon className="w-5 h-5 text-gray-500" />
                                                        ) : (
                                                            <ChevronDownIcon className="w-5 h-5 text-gray-500" />
                                                        )}
                                                    </div>
                                                </button>
                                                
                                                {/* Expanded Rolls Table */}
                                                {isExpanded && (
                                                    <div className="border-t border-gray-200 bg-gray-50 p-4">
                                                        {variant.rolls && variant.rolls.length > 0 ? (
                                                            <div className="space-y-2">
                                                                {variant.rolls.map((roll, rollIndex) => (
                                                                    <div key={rollIndex} className="flex items-center justify-between py-2 px-3 bg-white rounded border border-gray-200">
                                                                        <span className="text-sm text-gray-600">Rouleau {rollIndex + 1}</span>
                                                                        <span className="text-sm font-semibold text-gray-900">{roll.length}m</span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <p className="text-sm text-gray-500 text-center py-2">
                                                                Aucun rouleau disponible
                                                            </p>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Action Button */}
                        <button 
                            onClick={onClose}
                            className="w-full py-3 bg-gray-900 text-white font-medium text-sm uppercase tracking-wider hover:bg-gray-800 transition-colors"
                        >
                            Fermer
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductModal;
